import React, { useMemo, useState, Fragment, useCallback, useEffect, useDeferredValue } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableHead,
  TableRow,
  Collapse,
  Divider,
  Stack,
  useMediaQuery,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Edit,
  Map,
  Radar,
  AlertTriangle,
  Circle,
  ShieldAlert,
  Target,
  DoorOpen,
  DoorClosed,
  Bell,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Navigation,
  Trash2, // ✅ FIX: lucide has Trash2 (not TrashIcon)
  Search,
  UserRound,
  Users,
  Download,
  Upload,
  FileText,
  BadgeCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getDeviceActionsEffective } from "../../utils/rbacHelper";
import { downloadEntityTemplate, uploadEntityFile } from "../../services/importExportService";
import ScrollableTableContainer from "../common/ScrollableTableContainer";
import {
  useEditDeviceMutation,
  useGetAdminDevicesV2PageQuery,
  useGetDevicesPageQuery,
} from "../../redux/admin/adminApi";
import { useLazyGetDeviceV2ByIdQuery } from "../../redux/admin/deviceApi";
import { useGetAllGroupsQuery, useAddDevicesToGroupMutation } from "../../redux/admin/groupApi";
import NotificationFormModal from "../../mainframes/sidebar/NotificationFormModal";
import ReportsPortal from "../reports/ReportsPortal";
import { useStatus } from "../../popup/NotificationContext";
import InstallationCertificateModal from "./InstallationCertificateModal";
import { getReadableStatusMessage } from "../../utils/statusMessage";
import { mergeLegacyDeviceDetail } from "../../utils/deviceV2Adapter";
import { readDeviceViewCache, writeDeviceViewCache } from "../../utils/deviceViewCache";

// ✅ GEOFENCE ALARM TYPES CONFIG
const GEO_ALARM_CONFIG = {
  geofence_entry: {
    icon: DoorOpen,
    color: "#3b82f6",
    label: "Entry",
    bgColor: "#dbeafe",
    severity: "info",
  },
  geofence_exit: {
    icon: DoorClosed,
    color: "#8b5cf6",
    label: "Exit",
    bgColor: "#f5f3ff",
    severity: "medium",
  },
  geofence_violation: {
    icon: ShieldAlert,
    color: "#ef4444",
    label: "Violation",
    bgColor: "#fee2e2",
    severity: "high",
  },
  geofence_overspeed: {
    icon: AlertTriangle,
    color: "#f97316",
    label: "Overspeed",
    bgColor: "#ffedd5",
    severity: "high",
  },
  geofence_idle: {
    icon: Bell,
    color: "#eab308",
    label: "Idle",
    bgColor: "#fef9c3",
    severity: "low",
  },
  geofence_dwell: {
    icon: Circle,
    color: "#10b981",
    label: "Dwell",
    bgColor: "#d1fae5",
    severity: "medium",
  },
};

const DEVICE_MANAGEMENT_PAGE_CACHE_SCOPE = "device-management-page";

// ---------- helpers ----------
function safeStringify(obj) {
  try {
    return JSON.stringify(obj);
  } catch {
    return "—";
  }
}

function unwrapMongo(value) {
  if (value && typeof value === "object") {
    if ("$oid" in value) return value.$oid;
    if ("$date" in value) return value.$date;
  }
  return value;
}

function toIdString(value) {
  const unwrapped = unwrapMongo(value);
  if (!unwrapped) return "";
  if (typeof unwrapped === "string") return unwrapped;
  if (typeof unwrapped === "object") {
    return String(unwrapped._id || unwrapped.id || "");
  }
  return String(unwrapped);
}

function formatDate(value) {
  if (!value) return "—";
  const v = unwrapMongo(value);
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(v);
  }
}

function formatValue(value, key) {
  if (value === null || value === undefined) return "—";
  const k = String(key || "").toLowerCase();

  value = unwrapMongo(value);

  if (k.includes("date") || k.includes("time") || k.endsWith("at")) {
    return formatDate(value);
  }

  if (k.includes("assignedto") || k.includes("by") || k.includes("user")) {
    const v = unwrapMongo(value);
    if (v && typeof v === "object") {
      if (v.username || v.name || v.email) return String(v.username || v.name || v.email);
      if (v._id) return String(unwrapMongo(v._id));
      return "—";
    }
    return String(v);
  }

  if (
    (value && typeof value === "object" && (value.address || value.lat != null || value.lng != null)) ||
    k === "source" ||
    k === "destination" ||
    k.includes("location") ||
    k.includes("address")
  ) {
    if (value && typeof value === "object") {
      const parts = [];
      if (value.address) parts.push(String(value.address));
      if (value.city) parts.push(String(value.city));
      if (value.state) parts.push(String(value.state));
      if (value.pincode) parts.push(String(value.pincode));
      if (value.country) parts.push(String(value.country));
      if (value.lat != null && value.lng != null) parts.push(`(${value.lat}, ${value.lng})`);
      return parts.length ? parts.join(", ") : "—";
    }
    return String(value);
  }

  if (Array.isArray(value)) {
    if (!value.length) return "—";
    if (typeof value[0] === "object" && value[0] !== null) return `${value.length} items`;
    return value.join(", ");
  }

  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (typeof value === "object") {
    if (value.name) return String(value.name);
    if (value.title) return String(value.title);
    if (value.value != null && value.voltage != null) return `v:${value.value}, volt:${value.voltage}`;
    return safeStringify(value);
  }

  return String(value);
}

function toLabel(key) {
  return String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

function getDynamicColumns(data) {
  if (!Array.isArray(data) || data.length === 0) return [];

  const exclude = new Set([
    "_id",
    "id",
    "__v",
    "liveData",
    "createdAt",
    "createdBy",
    "licenseRuntime",
    "geoLocation",
    "installationCertificate",
    "units",
    "totalRunningTime",
    "currentLocation",
    "source",
  ]);

  const keys = new Set();
  data.forEach((row) => {
    if (!row || typeof row !== "object") return;
    Object.keys(row).forEach((k) => {
      if (!exclude.has(k)) keys.add(k);
    });
  });

  const priority = ["name", "todaysOdo", "totalOdo", "initialOdometer", "status", "model", "imei", "msdn", "vehicleType"];
  const arr = Array.from(keys);

  arr.sort((a, b) => {
    const ai = priority.indexOf(a);
    const bi = priority.indexOf(b);
    if (ai !== -1 || bi !== -1) return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    return a.localeCompare(b);
  });

  return arr.map((key) => ({ key, label: toLabel(key) }));
}

function normalizeLicenseRuntime(device) {
  const lr = device?.licenseRuntime || {};
  const expiryDate = lr.expiryDate || device?.deviceLicense?.expiryDate || null;
  const expiresInDays =
    typeof lr.expiresInDays === "number" && Number.isFinite(lr.expiresInDays)
      ? lr.expiresInDays
      : null;
  const isExpired = Boolean(lr.isExpired);
  const isExpiringSoon = Boolean(lr.isExpiringSoon);
  return {
    expiryDate,
    expiresInDays,
    isExpired,
    isExpiringSoon,
    warningMessage: lr.warningMessage || null,
  };
}

function formatDeviceLicenseSummary(device) {
  const license = device?.deviceLicense || {};
  const runtime = normalizeLicenseRuntime(device);
  const plan =
    license.licenseType ||
    license.planName ||
    device?.licenseType ||
    device?.deviceLicenseType ||
    "Standard";

  if (runtime.isExpired) {
    return `Expired${runtime.expiryDate ? ` on ${formatDate(runtime.expiryDate)}` : ""}`;
  }

  if (runtime.expiryDate && Number.isFinite(runtime.expiresInDays)) {
    return `${plan} • expires in ${Math.max(0, runtime.expiresInDays)} day${Math.max(0, runtime.expiresInDays) === 1 ? "" : "s"}`;
  }

  if (runtime.expiryDate) {
    return `${plan} • valid till ${formatDate(runtime.expiryDate)}`;
  }

  if (license.isLifetime || String(plan).toLowerCase() === "lifetime") {
    return "Lifetime license";
  }

  return `${plan} license`;
}

function formatAddress(address) {
  if (!address) return null;
  if (typeof address === "string") return address;
  if (address.full) return address.full;

  const parts = [];
  if (address.street) parts.push(address.street);
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.country) parts.push(address.country);

  return parts.length > 0 ? parts.join(", ") : null;
}

function normalizeAddressText(value) {
  const text =
    formatAddress(value) ||
    (value && typeof value === "object" ? value.short || value.address || null : null);
  if (!text) return null;
  if (/^location at\s*-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/i.test(String(text).trim())) {
    return null;
  }
  return String(text).trim();
}

function getDeviceLiveAddress(device, resolvedAddress = "") {
  const live = device?.liveData || {};

  return (
    normalizeAddressText(resolvedAddress) ||
    normalizeAddressText(live?.address) ||
    normalizeAddressText(live?.currentAddress) ||
    normalizeAddressText(live?.locationAddress) ||
    normalizeAddressText(device?.address) ||
    ""
  );
}

function deriveDeviceCoordinates(device) {
  const live = device?.liveData || {};
  const current = device?.currentLocation || {};
  const locationCoords = Array.isArray(device?.location?.coordinates)
    ? device.location.coordinates
    : Array.isArray(current?.location?.coordinates)
      ? current.location.coordinates
      : null;

  const candidates = [
    [live?.latitude, live?.longitude],
    [current?.latitude, current?.longitude],
    [device?.latitude, device?.longitude],
    [locationCoords?.[1], locationCoords?.[0]],
  ];

  for (const [latRaw, lngRaw] of candidates) {
    const lat = Number(latRaw);
    const lng = Number(lngRaw);
    if (Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)) {
      return { lat, lng };
    }
  }

  return null;
}

function formatLiveDataSummary(device, resolvedAddress = "") {
  const live = device?.liveData || {};
  const parts = [];
  const address = getDeviceLiveAddress(device, resolvedAddress);

  if (address) {
    parts.push(address);
  }

  if (live?.vehicleStatus) parts.push(`Status: ${String(live.vehicleStatus)}`);
  else if (live?.accStatus === true) parts.push("Ignition On");
  else if (live?.accStatus === false) parts.push("Ignition Off");

  if (Number.isFinite(Number(live?.speed))) parts.push(`Speed: ${Number(live.speed)} km/h`);

  return parts.filter(Boolean).join(" | ") || "No live data";
}

function formatLiveDataPrimaryLine(device, resolvedAddress = "", addressStatus = "") {
  const address = getDeviceLiveAddress(device, resolvedAddress);
  const coords = deriveDeviceCoordinates(device);
  const lat = Number(coords?.lat);
  const lng = Number(coords?.lng);

  if (address) {
    return address;
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return "No location";
  }

  if (addressStatus === "loading") {
    return "Loading...";
  }

  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

function getStatusChipColor(status) {
  const s = String(status || "").toLowerCase();
  if (["online", "moving", "running"].includes(s)) return "success";
  if (["idle"].includes(s)) return "warning";
  if (["offline", "inactive"].includes(s)) return "error";
  if (["deleted"].includes(s)) return "default";
  return "default";
}

// ---------- component ----------
function DeviceManagement({
  devices = [],
  assignableUsersForDevices = [],
  formatTimestamp12h,
  setEditingDevice,
  setShowEditDeviceModal,
  handleGeofenceManagement,
  handleSensorManagement,
  handleDeleteDevice,
  currentUserRole,
  setShowRegisterDeviceModal,
  currentUser,
  geofenceAlarms = [],
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const [editDevice] = useEditDeviceMutation();
  const [addDevicesToGroup, { isLoading: isAssigningGroup }] = useAddDevicesToGroupMutation();
  const { data: allGroupsResponse } = useGetAllGroupsQuery();
  const { showStatus } = useStatus();

  // ✅ FIX: search/filter states were not usable (no setter used). Now fully working.
  const [deviceSearchQuery, setDeviceSearchQuery] = useState("");
  const [deviceAssignedToFilter, setDeviceAssignedToFilter] = useState("all");
  const [expandedDeviceId, setExpandedDeviceId] = useState(null);
  const [editingOdo, setEditingOdo] = useState(null);
  const [editingOdoValue, setEditingOdoValue] = useState("");
  const [fleetUploading, setFleetUploading] = useState(false);
  const [resolvedAddressMap, setResolvedAddressMap] = useState({});
  const [addressRequestState, setAddressRequestState] = useState({});
  const [deviceDetailCache, setDeviceDetailCache] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [assignUserDevice, setAssignUserDevice] = useState(null);
  const [selectedAssignUserId, setSelectedAssignUserId] = useState("");
  const [assignGroupDevice, setAssignGroupDevice] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [notificationDevice, setNotificationDevice] = useState(null);
  const [reportDevice, setReportDevice] = useState(null);
  const [certificateDevice, setCertificateDevice] = useState(null);
  const fleetUploadInputRef = React.useRef(null);
  const geocodeMetaRef = React.useRef({});
  const deferredDeviceSearchQuery = useDeferredValue(deviceSearchQuery);
  const pageQueryArgs = useMemo(
    () => ({
      page: page + 1,
      limit: Math.min(rowsPerPage, 100),
      search: String(deferredDeviceSearchQuery || "").trim() || undefined,
      assignedToUser:
        deviceAssignedToFilter && deviceAssignedToFilter !== "all"
          ? deviceAssignedToFilter
          : undefined,
    }),
    [deferredDeviceSearchQuery, deviceAssignedToFilter, page, rowsPerPage]
  );
  const [cachedDevicesPage, setCachedDevicesPage] = useState(null);
  const {
    data: devicesPageV2,
    isLoading: isDevicesPageLoading,
    isFetching: isDevicesPageFetching,
    isError: isDevicesV2PageError,
  } = useGetAdminDevicesV2PageQuery(pageQueryArgs);
  const { data: devicesPageFallback } = useGetDevicesPageQuery(pageQueryArgs, {
    skip: !isDevicesV2PageError,
  });
  const devicesPage = devicesPageV2 || devicesPageFallback || cachedDevicesPage;
  const [loadDeviceDetail] = useLazyGetDeviceV2ByIdQuery();

  useEffect(() => {
    const cachedPage = readDeviceViewCache(DEVICE_MANAGEMENT_PAGE_CACHE_SCOPE, {
      userId: currentUser?._id,
      params: pageQueryArgs,
    });

    if (cachedPage?.data && Array.isArray(cachedPage.data)) {
      setCachedDevicesPage(cachedPage);
      return;
    }

    setCachedDevicesPage(null);
  }, [currentUser?._id, pageQueryArgs]);

  useEffect(() => {
    const nextPagePayload = devicesPageV2 || devicesPageFallback;
    if (!nextPagePayload?.data || !Array.isArray(nextPagePayload.data)) return;

    writeDeviceViewCache(DEVICE_MANAGEMENT_PAGE_CACHE_SCOPE, {
      userId: currentUser?._id,
      params: pageQueryArgs,
      payload: nextPagePayload,
    });
  }, [currentUser?._id, devicesPageFallback, devicesPageV2, pageQueryArgs]);

  const safeDevices = useMemo(() => {
    if (Array.isArray(devicesPage?.data)) return devicesPage.data;
    return [];
  }, [devicesPage?.data]);
  const hydratedDevices = useMemo(
    () =>
      safeDevices.map((device) =>
        deviceDetailCache[String(device?._id || device?.id || "")]
          ? mergeLegacyDeviceDetail(
              device,
              deviceDetailCache[String(device?._id || device?.id || "")]
            )
          : device
      ),
    [deviceDetailCache, safeDevices]
  );
  const totalDevices = useMemo(() => {
    if (Number.isFinite(Number(devicesPage?.total))) return Number(devicesPage.total);
    return safeDevices.length;
  }, [devicesPage?.total, safeDevices.length]);
  const availableGroups = useMemo(() => {
    const rows = allGroupsResponse?.data || allGroupsResponse || [];
    return Array.isArray(rows) ? rows : [];
  }, [allGroupsResponse]);
  const actionIconSx = useCallback(
    (tone = "default") => {
      const tones = {
        default: {
          color: theme.palette.text.secondary,
          bg: alpha(theme.palette.text.primary, 0.05),
          hoverBg: alpha(theme.palette.text.primary, 0.12),
        },
        primary: {
          color: theme.palette.primary.main,
          bg: alpha(theme.palette.primary.main, 0.12),
          hoverBg: alpha(theme.palette.primary.main, 0.2),
        },
        info: {
          color: theme.palette.info.main,
          bg: alpha(theme.palette.info.main, 0.12),
          hoverBg: alpha(theme.palette.info.main, 0.2),
        },
        secondary: {
          color: theme.palette.secondary.main,
          bg: alpha(theme.palette.secondary.main, 0.12),
          hoverBg: alpha(theme.palette.secondary.main, 0.2),
        },
        success: {
          color: theme.palette.success.main,
          bg: alpha(theme.palette.success.main, 0.12),
          hoverBg: alpha(theme.palette.success.main, 0.2),
        },
        warning: {
          color: theme.palette.warning.main,
          bg: alpha(theme.palette.warning.main, 0.12),
          hoverBg: alpha(theme.palette.warning.main, 0.2),
        },
        error: {
          color: theme.palette.error.main,
          bg: alpha(theme.palette.error.main, 0.12),
          hoverBg: alpha(theme.palette.error.main, 0.2),
        },
      };

      const palette = tones[tone] || tones.default;
      return {
        color: palette.color,
        backgroundColor: palette.bg,
        border: `1px solid ${alpha(palette.color, 0.16)}`,
        transition: "all 0.18s ease",
        "&:hover": {
          backgroundColor: palette.hoverBg,
          transform: "translateY(-1px)",
        },
        "&.Mui-disabled": {
          color: alpha(palette.color, 0.4),
          backgroundColor: alpha(palette.color, 0.05),
          borderColor: alpha(palette.color, 0.08),
        },
      };
    },
    [theme],
  );


const deviceActions = useMemo(
  () => getDeviceActionsEffective(currentUser), // ✅ per-user CRUD applied
  [currentUser]
);

  const canRegisterDevices = useMemo(() => {
    return !!deviceActions.canCreate;
  }, [deviceActions.canCreate]);

  const getDeviceGeofenceAlarms = useCallback(
    (deviceId) => {
      if (!Array.isArray(geofenceAlarms)) return [];
      return geofenceAlarms
        .filter((alarm) => alarm?.deviceId === deviceId || alarm?.device?._id === deviceId)
        .sort((a, b) => new Date(b?.triggeredAt || 0) - new Date(a?.triggeredAt || 0));
    },
    [geofenceAlarms]
  );

  const getDeviceActiveGeofences = useCallback((device) => {
    if (!device?.geofences) return [];
    return device.geofences.filter((g) => g?.isActive === true);
  }, []);

  const formatOdoCell = useCallback((value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "0";
    return n.toFixed(2).replace(/\.00$/, "");
  }, []);

  const beginOdoEdit = useCallback((device, field) => {
    const deviceId = device?._id || device?.id;
    if (!deviceId) return;
    setEditingOdo({ deviceId, field });
    setEditingOdoValue(String(Number(device?.[`${field}RawKm`] ?? device?.[field] ?? 0) || 0));
  }, []);

  const cancelOdoEdit = useCallback(() => {
    setEditingOdo(null);
    setEditingOdoValue("");
  }, []);

  const refreshDeviceDetailCache = useCallback(
    async (deviceId) => {
      const normalizedDeviceId = String(deviceId || "").trim();
      if (!normalizedDeviceId) return null;
      const detailed = await loadDeviceDetail(normalizedDeviceId, false).unwrap();
      if (detailed) {
        setDeviceDetailCache((prev) => ({ ...prev, [normalizedDeviceId]: detailed }));
      }
      return detailed || null;
    },
    [loadDeviceDetail]
  );

  const saveOdoEdit = useCallback(async () => {
    if (!editingOdo?.deviceId || !editingOdo?.field) return;
    const numericValue = Math.max(0, Number(editingOdoValue) || 0);
    const fieldKey = editingOdo.field;
    const payload =
      fieldKey === "todaysOdo"
        ? { deviceId: editingOdo.deviceId, todaysOdo: numericValue }
        : { deviceId: editingOdo.deviceId, totalOdo: numericValue };

    try {
      await editDevice(payload).unwrap();
      setDeviceDetailCache((prev) => {
        const existing = prev[editingOdo.deviceId];
        if (!existing) return prev;
        return {
          ...prev,
          [editingOdo.deviceId]: {
            ...existing,
            [fieldKey]: numericValue,
            [`${fieldKey}RawKm`]: numericValue,
          },
        };
      });
      await refreshDeviceDetailCache(editingOdo.deviceId);
      cancelOdoEdit();
      window.dispatchEvent(new Event("app:crud-changed"));
    } catch (error) {
      showStatus("error", getReadableStatusMessage(error, "error"));
    }
  }, [cancelOdoEdit, editDevice, editingOdo, editingOdoValue, refreshDeviceDetailCache, showStatus]);

  const openAssignUserDialog = useCallback((device) => {
    setAssignUserDevice(device);
    setSelectedAssignUserId(device?.assignedToUser?._id || device?.assignedToUser || "");
  }, []);

  const closeAssignUserDialog = useCallback(() => {
    setAssignUserDevice(null);
    setSelectedAssignUserId("");
  }, []);

  const submitAssignUser = useCallback(async () => {
    const deviceId = assignUserDevice?._id || assignUserDevice?.id;
    if (!deviceId) return;

    try {
      await editDevice({
        deviceId,
        assignedToUser: selectedAssignUserId || null,
      }).unwrap();
      showStatus("success", selectedAssignUserId ? "Device assigned to the selected user." : "Device unassigned successfully.");
      closeAssignUserDialog();
      window.dispatchEvent(new Event("app:crud-changed"));
    } catch (error) {
      showStatus("error", getReadableStatusMessage(error, "error"));
    }
  }, [assignUserDevice, closeAssignUserDialog, editDevice, selectedAssignUserId, showStatus]);

  const openAssignGroupDialog = useCallback((device) => {
    setAssignGroupDevice(device);
    setSelectedGroupId("");
  }, []);

  const closeAssignGroupDialog = useCallback(() => {
    setAssignGroupDevice(null);
    setSelectedGroupId("");
  }, []);

  const submitAssignGroup = useCallback(async () => {
    const deviceId = assignGroupDevice?._id || assignGroupDevice?.id;
    if (!deviceId || !selectedGroupId) {
      showStatus("warning", "Select a group before assigning this device.");
      return;
    }

    try {
      await addDevicesToGroup({
        groupId: selectedGroupId,
        deviceIds: [deviceId],
      }).unwrap();
      showStatus("success", "Device assigned to the selected group.");
      closeAssignGroupDialog();
      window.dispatchEvent(new Event("app:crud-changed"));
    } catch (error) {
      showStatus("error", getReadableStatusMessage(error, "error"));
    }
  }, [addDevicesToGroup, assignGroupDevice, closeAssignGroupDialog, selectedGroupId, showStatus]);

  const formatGeofenceTime = useCallback((timestamp) => {
    if (!timestamp) return "Unknown";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString("en-IN");
  }, []);

  const onDownloadFleetTemplate = useCallback(async () => {
    try {
      await downloadEntityTemplate("fleets");
    } catch (error) {
      alert(error?.response?.data?.message || "Fleet template download failed.");
    }
  }, []);

  const onFleetFileSelected = useCallback(async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    setFleetUploading(true);
    try {
      const result = await uploadEntityFile("fleets", file, { validateOnly: false });
      alert(result?.message || "Fleet upload completed.");
    } catch (error) {
      const payload = error?.response?.data;
      const details = Array.isArray(payload?.errors) ? payload.errors.slice(0, 5).join("\n") : "";
      alert(`${payload?.message || "Fleet upload failed."}${details ? `\n${details}` : ""}`);
    } finally {
      if (fleetUploadInputRef.current) fleetUploadInputRef.current.value = "";
      setFleetUploading(false);
    }
  }, []);

  const columns = useMemo(() => getDynamicColumns(hydratedDevices), [hydratedDevices]);

  // ✅ FIX: colSpan mismatch bugs happen when mobile hides some columns.
  const visibleColumns = useMemo(() => {
    return columns.filter((c) => !(isMobile && (c.key === "assignedToViewers" || c.key === "geofences")));
  }, [columns, isMobile]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalDevices / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(0);
    }
  }, [page, rowsPerPage, totalDevices]);

  useEffect(() => {
    setPage(0);
  }, [deferredDeviceSearchQuery, deviceAssignedToFilter]);

  useEffect(() => {
    let cancelled = false;

    const fetchAddresses = async () => {
      const candidates = hydratedDevices
        .map((device) => {
          const deviceId = String(device?._id || device?.id || "");
          const existingAddress = getDeviceLiveAddress(device);
          const coords = deriveDeviceCoordinates(device);
          const lat = Number(coords?.lat);
          const lng = Number(coords?.lng);

          const alreadyResolved = Boolean(resolvedAddressMap[deviceId]);
          const requestState = addressRequestState[deviceId];
          const meta = geocodeMetaRef.current[deviceId];
          const elapsed = meta?.at ? Date.now() - meta.at : Infinity;
          const sameCoords =
            meta &&
            Math.abs(Number(meta.lat) - lat) < 0.0002 &&
            Math.abs(Number(meta.lng) - lng) < 0.0002;
          const alreadyRequested =
            requestState === "loading" ||
            requestState === "success" ||
            (requestState === "failed" && sameCoords && elapsed < 45000);

          if (
            !deviceId ||
            existingAddress ||
            alreadyResolved ||
            alreadyRequested ||
            !Number.isFinite(lat) ||
            !Number.isFinite(lng) ||
            (lat === 0 && lng === 0)
          ) {
            return null;
          }
          return { deviceId, lat, lng };
        })
        .filter(Boolean);

      if (!candidates.length) return;

      setAddressRequestState((prev) => ({
        ...prev,
        ...Object.fromEntries(candidates.map(({ deviceId }) => [deviceId, "loading"])),
      }));

      const token = localStorage.getItem("token");
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "https://qylmox.in/api";

      for (const { deviceId, lat, lng } of candidates) {
        if (cancelled) return;

        let status = "failed";
        let resolved = "";

        try {
          const response = await fetch(
            `${baseUrl}/devices/${deviceId}/address?lat=${lat}&lng=${lng}`,
            { headers: { "x-auth-token": token, "Content-Type": "application/json" } }
          );

          if (!response.ok) throw new Error("Address fetch failed");
          const result = await response.json();
          resolved = normalizeAddressText(result?.address) || "";
          status = resolved ? "success" : "failed";
        } catch {
          status = "failed";
        }

        if (cancelled) return;

        geocodeMetaRef.current[deviceId] = { lat, lng, at: Date.now() };
        setAddressRequestState((prev) => ({ ...prev, [deviceId]: status }));
        if (resolved) {
          setResolvedAddressMap((prev) => ({ ...prev, [deviceId]: resolved }));
        }

        await new Promise((resolve) => setTimeout(resolve, 120));
      }
    };

    fetchAddresses();
    return () => {
      cancelled = true;
    };
  }, [addressRequestState, hydratedDevices, resolvedAddressMap]);

  const ensureDeviceDetail = useCallback(
    async (device) => {
      const deviceId = String(device?._id || device?.id || "").trim();
      if (!deviceId) return device;

      const cached = deviceDetailCache[deviceId];
      if (cached) return mergeLegacyDeviceDetail(device, cached);

      const detailed = await loadDeviceDetail(deviceId, true).unwrap();
      if (detailed) {
        setDeviceDetailCache((prev) => ({ ...prev, [deviceId]: detailed }));
        return mergeLegacyDeviceDetail(device, detailed);
      }
      return device;
    },
    [deviceDetailCache, loadDeviceDetail]
  );

  useEffect(() => {
    let cancelled = false;

    const hydrateVisibleRows = async () => {
      const targets = safeDevices
        .filter((device) => {
          const id = String(device?._id || device?.id || "").trim();
          return id && !deviceDetailCache[id];
        })
        .slice(0, Math.min(safeDevices.length, rowsPerPage));

      if (!targets.length) return;

      const results = await Promise.allSettled(
        targets.map((device) => loadDeviceDetail(String(device?._id || device?.id), true).unwrap())
      );
      if (cancelled) return;

      const nextEntries = {};
      results.forEach((result, index) => {
        if (result.status !== "fulfilled" || !result.value) return;
        const source = targets[index];
        const id = String(source?._id || source?.id || "").trim();
        if (!id) return;
        nextEntries[id] = result.value;
      });

      if (Object.keys(nextEntries).length) {
        setDeviceDetailCache((prev) => ({ ...prev, ...nextEntries }));
      }
    };

    hydrateVisibleRows();
    return () => {
      cancelled = true;
    };
  }, [deviceDetailCache, loadDeviceDetail, rowsPerPage, safeDevices]);

  const toggleDeviceExpansion = useCallback(
    async (device) => {
      const deviceId = device?._id || device?.id;
      if (!deviceId) return;
      if (expandedDeviceId === deviceId) {
        setExpandedDeviceId(null);
        return;
      }
      setExpandedDeviceId(deviceId);
      try {
        await ensureDeviceDetail(device);
      } catch (_) {
        // Keep expansion working with slim row if detail fetch fails.
      }
    },
    [ensureDeviceDetail, expandedDeviceId]
  );

  if (!currentUser || !currentUserRole) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Typography>Loading...</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2.5 },
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
        minWidth: 0, // ✅ important for flex children to shrink + avoid overflow
      }}
    >
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight={900} noWrap>
              Device Fleet Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Monitor, track, and manage your GPS hardware assets.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.25} alignItems="center" justifyContent={{ xs: "space-between", md: "flex-end" }}>
            <Chip
              label={
                isDevicesPageFetching && !isDevicesPageLoading
                  ? `Total: ${totalDevices} • Refreshing`
                  : `Total: ${totalDevices}`
              }
              color="primary"
              variant="outlined"
            />
            <Button variant="outlined" startIcon={<Download size={16} />} onClick={onDownloadFleetTemplate}>
              Fleet Template
            </Button>
            <Button
              variant="outlined"
              startIcon={<Upload size={16} />}
              onClick={() => fleetUploadInputRef.current?.click()}
              disabled={fleetUploading}
            >
              {fleetUploading ? "Uploading..." : "Upload Fleet"}
            </Button>
            {canRegisterDevices && setShowRegisterDeviceModal && (
              <Button variant="contained" onClick={() => setShowRegisterDeviceModal(true)}>
                + Register New Device
              </Button>
            )}
            <input
              ref={fleetUploadInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: "none" }}
              onChange={onFleetFileSelected}
            />
          </Stack>
        </Stack>

        {/* ✅ Filters / Inputs area (scrollable when many inputs) */}
        <Box
          sx={{
            mt: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            p: 1.5,
            // ✅ If more filters added later, this area can scroll
            maxHeight: { xs: 190, sm: 220 },
            overflowY: "auto",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            sx={{
              width: "100%",
              minWidth: 0,
              // ✅ allow inputs to wrap on desktop, but still look clean
              flexWrap: { sm: "wrap" },
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <TextField
              value={deviceSearchQuery}
              onChange={(e) => setDeviceSearchQuery(e.target.value)}
              placeholder="Search by name, model, IMEI, MSDN..."
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: "1 1 320px",
                minWidth: { xs: "100%", sm: 280 },
              }}
            />

            <FormControl
              size="small"
              sx={{
                flex: "0 0 auto",
                minWidth: { xs: "100%", sm: 240 },
              }}
              fullWidth={isMobile}
            >
              <InputLabel id="assigned-to-filter">Assigned To</InputLabel>
              <Select
                labelId="assigned-to-filter"
                label="Assigned To"
                value={deviceAssignedToFilter}
                onChange={(e) => setDeviceAssignedToFilter(e.target.value)}
                startAdornment={
                  isMdDown ? (
                    <InputAdornment position="start" sx={{ ml: 1 }}>
                      <UserRound size={16} />
                    </InputAdornment>
                  ) : null
                }
              >
                <MenuItem value="all">All</MenuItem>
                {assignableUsersForDevices.map((u) => (
                  <MenuItem key={u?._id} value={u?._id}>
                    {u?.username || u?.name || u?.email || u?._id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={() => {
                setDeviceSearchQuery("");
                setDeviceAssignedToFilter("all");
              }}
              sx={{ flex: "0 0 auto" }}
            >
              Reset
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Table */}
      <Paper
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          minWidth: 0,
        }}
      >
        <ScrollableTableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            overflowX: "auto", // ✅ horizontal scroll for many columns
          }}
        >
          <Table size={isMobile ? "small" : "medium"} stickyHeader sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: 56,
                    fontWeight: 900,
                    position: "sticky",
                    left: 0,
                    zIndex: 6,
                    backgroundColor: "background.paper",
                  }}
                >
                  {" "}
                </TableCell>

                {visibleColumns.map((col) => (
                  <TableCell
                    key={col.key}
                    sx={{
                      fontWeight: 900,
                      whiteSpace: "nowrap",
                      ...(String(col.key).toLowerCase() === "name"
                        ? {
                            position: "sticky",
                            left: 56,
                            zIndex: 5,
                            backgroundColor: "background.paper",
                            minWidth: 180,
                          }
                        : {}),
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}

                <TableCell sx={{ width: 220, fontWeight: 900, whiteSpace: "nowrap" }}>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isDevicesPageLoading ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 2} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">Loading devices...</Typography>
                  </TableCell>
                </TableRow>
              ) : safeDevices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 2} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No devices found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                hydratedDevices.map((device) => {
                  const deviceId = device?._id || device?.id;
                  const isExpanded = expandedDeviceId === deviceId;
                  const licenseRuntime = normalizeLicenseRuntime(device);

                  const deviceGeofenceAlarms = getDeviceGeofenceAlarms(deviceId);
                  const activeGeofences = getDeviceActiveGeofences(device);

                  const hasGeofences = activeGeofences.length > 0;
                  const hasGeofenceAlarms = deviceGeofenceAlarms.length > 0;

                  return (
                    <Fragment key={deviceId || Math.random()}>
                      <TableRow hover>
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            zIndex: 4,
                            backgroundColor: "background.paper",
                            width: 56,
                          }}
                        >
                          <Tooltip title={isExpanded ? "Collapse" : "Expand"}>
                            <span>
                              <IconButton size="small" onClick={() => deviceId && toggleDeviceExpansion(device)} disabled={!deviceId}>
                                {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>

                        {visibleColumns.map((col) => {
                          const raw = device?.[col.key];

                          if (String(col.key).toLowerCase() === "status") {
                            return (
                              <TableCell key={col.key}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                                  <Chip
                                    size="small"
                                    label={formatValue(raw, col.key)}
                                    color={getStatusChipColor(raw)}
                                    variant="filled"
                                  />
                                  {licenseRuntime.isExpired && (
                                    <Chip size="small" label="License Expired" color="error" variant="outlined" />
                                  )}
                                  {!licenseRuntime.isExpired && Number.isFinite(licenseRuntime.expiresInDays) && (
                                    <Chip
                                      size="small"
                                      label={
                                        licenseRuntime.isExpiringSoon
                                          ? `Expiring in ${licenseRuntime.expiresInDays}d`
                                          : `License ${licenseRuntime.expiresInDays}d left`
                                      }
                                      color={licenseRuntime.isExpiringSoon ? "warning" : "success"}
                                      variant="outlined"
                                    />
                                  )}
                                  {hasGeofenceAlarms && (
                                    <Chip
                                      size="small"
                                      icon={<AlertCircle size={14} />}
                                      label={deviceGeofenceAlarms.length}
                                      color="error"
                                      variant="outlined"
                                      onClick={() => deviceId && toggleDeviceExpansion(device)}
                                      sx={{ cursor: "pointer" }}
                                    />
                                  )}
                                  {hasGeofences && !hasGeofenceAlarms && (
                                    <Chip
                                      size="small"
                                      icon={<Target size={14} />}
                                      label={activeGeofences.length}
                                      color="info"
                                      variant="outlined"
                                      onClick={() => deviceId && toggleDeviceExpansion(device)}
                                      sx={{ cursor: "pointer" }}
                                    />
                                  )}
                                </Stack>
                              </TableCell>
                            );
                          }

                          if (String(col.key).toLowerCase() === "name") {
                            return (
                              <TableCell
                                key={col.key}
                                sx={{
                                  position: "sticky",
                                  left: 56,
                                  zIndex: 3,
                                  backgroundColor: "background.paper",
                                  minWidth: 180,
                                }}
                              >
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                  <Navigation size={18} />
                                  <Typography variant="body2" fontWeight={800} noWrap>
                                    {formatValue(raw, col.key)}
                                  </Typography>
                                </Stack>
                              </TableCell>
                            );
                          }

                          if (String(col.key).toLowerCase() === "lastdatareceivedat") {
                            return (
                              <TableCell key={col.key} sx={{ whiteSpace: "nowrap" }}>
                                {formatTimestamp12h ? formatTimestamp12h(raw) : formatValue(raw, col.key)}
                              </TableCell>
                            );
                          }

                          if (col.key === "liveData") {
                            const resolvedAddress = resolvedAddressMap[String(deviceId || "")] || "";
                            const addressStatus = addressRequestState[String(deviceId || "")] || "";
                            return (
                              <TableCell
                                key={col.key}
                                sx={{
                                  minWidth: 280,
                                  maxWidth: 420,
                                }}
                                title={formatLiveDataSummary(device, resolvedAddress)}
                              >
                                <Stack spacing={0.5}>
                                  <Typography variant="body2" fontWeight={700}>
                                    {formatLiveDataPrimaryLine(device, resolvedAddress, addressStatus)}
                                    {` | Status: ${
                                      device?.liveData?.vehicleStatus ||
                                      device?.vehicleStatus ||
                                      "no_data"
                                    } | Speed: ${Number(device?.liveData?.speed || 0)} km/h`}
                                  </Typography>
                                  {device?.lastDataReceivedAt && (
                                    <Typography variant="caption" color="text.secondary">
                                      Updated: {formatTimestamp12h ? formatTimestamp12h(device.lastDataReceivedAt) : formatDate(device.lastDataReceivedAt)}
                                    </Typography>
                                  )}
                                </Stack>
                              </TableCell>
                            );
                          }

                          if (col.key === "deviceLicense") {
                            return (
                              <TableCell key={col.key} sx={{ minWidth: 250 }}>
                                <Stack spacing={0.75}>
                                  <Typography variant="body2" fontWeight={700}>
                                    {formatDeviceLicenseSummary(device)}
                                  </Typography>
                                  <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
                                    <Chip
                                      size="small"
                                      label={licenseRuntime.isExpired ? "Expired" : "Active"}
                                      color={licenseRuntime.isExpired ? "error" : "success"}
                                      variant="outlined"
                                    />
                                    {licenseRuntime.expiryDate && (
                                      <Chip
                                        size="small"
                                        label={`Expiry: ${formatDate(licenseRuntime.expiryDate)}`}
                                        variant="outlined"
                                      />
                                    )}
                                  </Stack>
                                </Stack>
                              </TableCell>
                            );
                          }

                          if (col.key === "todaysOdo" || col.key === "totalOdo") {
                            const isEditing =
                              editingOdo?.deviceId === deviceId && editingOdo?.field === col.key;

                            return (
                              <TableCell key={col.key} sx={{ whiteSpace: "nowrap", minWidth: 150 }}>
                                {isEditing ? (
                                  <Stack direction="row" spacing={0.75} alignItems="center">
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={editingOdoValue}
                                      onChange={(e) => setEditingOdoValue(e.target.value)}
                                      inputProps={{ min: 0, step: 0.01 }}
                                      sx={{ width: 96 }}
                                    />
                                    <Button size="small" variant="contained" onClick={saveOdoEdit}>
                                      Save
                                    </Button>
                                    <Button size="small" variant="outlined" onClick={cancelOdoEdit}>
                                      Cancel
                                    </Button>
                                  </Stack>
                                ) : (
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Typography variant="body2">{formatOdoCell(raw)} km</Typography>
                                    {deviceActions.canEdit && (
                                      <IconButton size="small" color="primary" onClick={() => beginOdoEdit(device, col.key)}>
                                        <Edit size={14} />
                                      </IconButton>
                                    )}
                                  </Stack>
                                )}
                              </TableCell>
                            );
                          }

                          if (col.key === "initialOdometer") {
                            return (
                              <TableCell key={col.key} sx={{ whiteSpace: "nowrap" }}>
                                {formatOdoCell(device?.initialOdometerRawKm ?? raw)} km
                              </TableCell>
                            );
                          }

                          return (
                            <TableCell
                              key={col.key}
                              sx={{
                                maxWidth: 320,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={formatValue(raw, col.key)}
                            >
                              {formatValue(raw, col.key)}
                            </TableCell>
                          );
                        })}

                        <TableCell sx={{ minWidth: 470 }}>
                          <Paper
                            variant="outlined"
                            sx={{
                              px: 1,
                              py: 0.75,
                              borderRadius: 2.5,
                              backgroundColor: "rgba(25, 118, 210, 0.04)",
                              borderColor: "rgba(25, 118, 210, 0.18)",
                            }}
                          >
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.75 }}>
                            {deviceActions.canEdit && (
                              <Tooltip title="Edit Device">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={async () => {
                                      const detailedDevice = await ensureDeviceDetail(device);
                                      setEditingDevice?.(detailedDevice);
                                      setShowEditDeviceModal?.(true);
                                    }}
                                    sx={actionIconSx("primary")}
                                  >
                                    <Edit size={16} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {deviceActions.canEdit && (
                              <Tooltip title="Installation Certificate">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={async () => setCertificateDevice(await ensureDeviceDetail(device))}
                                    sx={actionIconSx("success")}
                                  >
                                    <BadgeCheck size={16} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {deviceActions.canEdit && (
                              <Tooltip title="Assign User">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => openAssignUserDialog(device)}
                                    sx={actionIconSx("success")}
                                  >
                                    <UserRound size={16} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {deviceActions.canEdit && (
                              <Tooltip title="Assign Group">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => openAssignGroupDialog(device)}
                                    sx={actionIconSx("warning")}
                                  >
                                    <Users size={16} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {deviceActions.canManageGeofence && (
                              <Tooltip title="Manage Geofence">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={async () => handleGeofenceManagement?.(await ensureDeviceDetail(device))}
                                    sx={actionIconSx("info")}
                                  >
                                    <Map size={16} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {deviceActions.canEdit && (
                              <Tooltip title="Notification Settings">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={async () => setNotificationDevice(await ensureDeviceDetail(device))}
                                    sx={actionIconSx("info")}
                                  >
                                    <Bell size={16} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            <Tooltip title="Audit / Data Report">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={async () => setReportDevice(await ensureDeviceDetail(device))}
                                  sx={actionIconSx("default")}
                                >
                                  <FileText size={16} />
                                </IconButton>
                              </span>
                            </Tooltip>

                            {handleSensorManagement && (
                              <Tooltip title="Manage Sensors">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSensorManagement(device)}
                                    sx={actionIconSx("secondary")}
                                  >
                                    <Radar size={16} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {deviceActions.canDelete && (
                              <Tooltip title={String(device?.status || "").toLowerCase() === "deleted" ? "Delete Permanently" : "Delete Device"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => deviceId && handleDeleteDevice?.(device)}
                                    disabled={!deviceId}
                                    sx={actionIconSx("error")}
                                  >
                                    <Trash2 size={16} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            </Stack>
                          </Paper>
                        </TableCell>
                      </TableRow>

                      {/* Expanded row */}
                      <TableRow>
                        <TableCell
                          colSpan={
                            // expand cell + visible dynamic columns + actions
                            1 + visibleColumns.length + 1
                          }
                          sx={{ py: 0, borderBottom: "none" }}
                        >
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                              <Stack
                                direction={{ xs: "column", md: "row" }}
                                spacing={2}
                                divider={<Divider flexItem orientation={isMobile ? "horizontal" : "vertical"} />}
                              >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <AlertCircle size={18} />
                                    <Typography variant="subtitle1" fontWeight={900}>
                                      License Health
                                    </Typography>
                                  </Stack>
                                  <Stack spacing={1}>
                                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                                      <Chip
                                        size="small"
                                        label={licenseRuntime.isExpired ? "Expired" : "Active"}
                                        color={licenseRuntime.isExpired ? "error" : "success"}
                                        variant="outlined"
                                      />
                                      {licenseRuntime.expiryDate && (
                                        <Chip
                                          size="small"
                                          label={`Expiry: ${formatDate(licenseRuntime.expiryDate)}`}
                                          variant="outlined"
                                        />
                                      )}
                                      {Number.isFinite(licenseRuntime.expiresInDays) && (
                                        <Chip
                                          size="small"
                                          label={`${Math.max(0, licenseRuntime.expiresInDays)} days left`}
                                          color={licenseRuntime.isExpiringSoon ? "warning" : "default"}
                                          variant="outlined"
                                        />
                                      )}
                                    </Stack>
                                    {licenseRuntime.warningMessage ? (
                                      <Typography variant="caption" color="warning.main">
                                        {licenseRuntime.warningMessage}
                                      </Typography>
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">
                                        Warning starts automatically 7 days before expiry.
                                      </Typography>
                                    )}
                                  </Stack>
                                </Box>

                                {/* Active Geofences */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <Target size={18} />
                                    <Typography variant="subtitle1" fontWeight={900}>
                                      Active Geofences
                                    </Typography>
                                    <Chip size="small" label={activeGeofences.length} />
                                  </Stack>

                                  {activeGeofences.length > 0 ? (
                                    <Stack spacing={1} sx={{ maxHeight: 220, overflowY: "auto", pr: 0.5 }}>
                                      {activeGeofences.map((g, idx) => (
                                        <Paper
                                          key={`${deviceId}-geo-${idx}`}
                                          variant="outlined"
                                          sx={{
                                            p: 1.2,
                                            borderRadius: 2,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 1,
                                            minWidth: 0,
                                          }}
                                        >
                                          <Typography variant="body2" fontWeight={800} noWrap sx={{ minWidth: 0 }}>
                                            {g?.name || `Zone ${idx + 1}`}
                                          </Typography>
                                          <Chip size="small" label="Active" color="success" variant="outlined" />
                                        </Paper>
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No active geofences.
                                    </Typography>
                                  )}
                                </Box>

                                {/* Recent Alerts */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <AlertCircle size={18} />
                                    <Typography variant="subtitle1" fontWeight={900}>
                                      Recent Alerts
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={deviceGeofenceAlarms.length}
                                      color={hasGeofenceAlarms ? "error" : "default"}
                                      variant="outlined"
                                    />
                                  </Stack>

                                  {hasGeofenceAlarms ? (
                                    <Stack spacing={1} sx={{ maxHeight: 220, overflowY: "auto", pr: 0.5 }}>
                                      {deviceGeofenceAlarms.slice(0, 6).map((alarm, idx) => {
                                        const config = GEO_ALARM_CONFIG[alarm?.alarmType] || GEO_ALARM_CONFIG.geofence_violation;
                                        const Icon = config.icon;

                                        return (
                                          <Paper
                                            key={`${deviceId}-alarm-${idx}`}
                                            variant="outlined"
                                            onClick={() => console.log("Geofence alarm clicked:", alarm)}
                                            sx={{
                                              p: 1.2,
                                              borderRadius: 2,
                                              cursor: "pointer",
                                              display: "flex",
                                              gap: 1.2,
                                              alignItems: "center",
                                              minWidth: 0,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: 2,
                                                display: "grid",
                                                placeItems: "center",
                                                background: config.bgColor,
                                                color: config.color,
                                                flexShrink: 0,
                                              }}
                                            >
                                              <Icon size={16} />
                                            </Box>

                                            <Box sx={{ minWidth: 0 }}>
                                              <Typography variant="body2" fontWeight={900} noWrap>
                                                {config.label}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary" noWrap>
                                                {formatGeofenceTime(alarm?.triggeredAt)}
                                              </Typography>
                                            </Box>
                                          </Paper>
                                        );
                                      })}
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No recent alerts.
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </ScrollableTableContainer>
        <TablePagination
          component="div"
          count={totalDevices}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <Dialog open={Boolean(assignUserDevice)} onClose={closeAssignUserDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Assign Device To User</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary", mb: 2 }}>
            {assignUserDevice?.name || "Device"} ko primary user assign karo.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Select User</InputLabel>
            <Select
              value={selectedAssignUserId}
              label="Select User"
              onChange={(e) => setSelectedAssignUserId(e.target.value)}
            >
              <MenuItem value="">Unassign</MenuItem>
              {assignableUsersForDevices.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.username} ({user.role})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeAssignUserDialog}>Cancel</Button>
          <Button variant="contained" onClick={submitAssignUser}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(assignGroupDevice)} onClose={closeAssignGroupDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Assign Device To Group</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary", mb: 2 }}>
            {assignGroupDevice?.name || "Device"} ko group me add karo.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Select Group</InputLabel>
            <Select
              value={selectedGroupId}
              label="Select Group"
              onChange={(e) => setSelectedGroupId(e.target.value)}
            >
              {availableGroups.map((group) => (
                <MenuItem key={group._id} value={group._id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeAssignGroupDialog}>Cancel</Button>
          <Button variant="contained" onClick={submitAssignGroup} disabled={!selectedGroupId || isAssigningGroup}>
            {isAssigningGroup ? "Assigning..." : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationFormModal
        open={Boolean(notificationDevice)}
        onClose={() => setNotificationDevice(null)}
        presetDevice={notificationDevice}
        titleOverride={
          notificationDevice ? `Notification: ${notificationDevice.name || notificationDevice.imei || "Device"}` : undefined
        }
      />

      {reportDevice ? (
        <ReportsPortal
          isOpen
          onClose={() => setReportDevice(null)}
          allDevices={[reportDevice]}
          rawData={[reportDevice]}
        />
      ) : null}

      <InstallationCertificateModal
        open={Boolean(certificateDevice)}
        onClose={() => setCertificateDevice(null)}
        device={certificateDevice}
      />
    </Box>
  );
}

export default DeviceManagement;
